/**
 * Waiting and racing that an `AbortSignal` can actually cut short.
 *
 * The connection paths in `service.ts` retry with backoff behind a caller's
 * signal, and the platform gives no cancellable sleep — a bare `setTimeout`
 * keeps a torn-down attempt alive for its full delay, so an aborted connect
 * carried on retrying against an environment the caller had already left.
 *
 * All three reject with the SAME `AbortError`, which is what lets a caller
 * treat cancellation as one outcome instead of three: `error.name` is the
 * check, matching what `fetch` throws for the same reason.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, ms));
}

function createAbortError(): Error {
  const error = new Error("The environment connection attempt was cancelled.");
  error.name = "AbortError";
  return error;
}

export function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw createAbortError();
  }
}

export function raceWithAbort<T>(
  operation: Promise<T>,
  signal: AbortSignal | undefined,
): Promise<T> {
  if (!signal) return operation;
  if (signal.aborted) return Promise.reject(createAbortError());

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(createAbortError());
    signal.addEventListener("abort", onAbort, { once: true });
    operation.then(resolve, reject).finally(() => {
      signal.removeEventListener("abort", onAbort);
    });
  });
}

export function sleepWithAbort(ms: number, signal: AbortSignal | undefined): Promise<void> {
  if (!signal) return sleep(ms);
  if (signal.aborted) return Promise.reject(createAbortError());

  return new Promise<void>((resolve, reject) => {
    const timeoutId = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      globalThis.clearTimeout(timeoutId);
      reject(createAbortError());
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}
